"use client";

import { c as _c } from "react/compiler-runtime";
import { useRef, useEffect } from "react";
import { Send } from "lucide-react";
import { strings } from "@/lib/strings";
interface ChatInputProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onSubmit: (e?: React.FormEvent) => void;
  isLoading: boolean;
  /** Optional placeholder override (e.g. "z.B. 40213" when the bot is asking for PLZ) */
  placeholder?: string;
}

/**
 * Chat input with auto-resizing textarea and send button.
 * Enter sends, Shift+Enter creates a new line.
 */
export function ChatInput(t0) {
  const $ = _c(31);
  const {
    value,
    onChange,
    onSubmit,
    isLoading,
    placeholder
  } = t0;
  const textareaRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const ta = textareaRef.current;
      if (ta) {
        ta.style.height = "auto";
        ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`;
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== value) {
    t2 = [value];
    $[1] = value;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[3] !== isLoading) {
    t3 = () => {
      if (isLoading) {
        return;
      }
      const active = document.activeElement;
      const typingElsewhere = active !== textareaRef.current && (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement);
      if (!typingElsewhere) {
        textareaRef.current?.focus();
      }
    };
    t4 = [isLoading];
    $[3] = isLoading;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  if ($[6] !== isLoading || $[7] !== onSubmit || $[8] !== value) {
    t5 = e => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (!isLoading && value.trim()) {
          onSubmit();
        }
      }
    };
    $[6] = isLoading;
    $[7] = onSubmit;
    $[8] = value;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const handleKeyDown = t5;
  let t6;
  if ($[10] !== onSubmit) {
    t6 = e_0 => {
      e_0.preventDefault();
      onSubmit();
    };
    $[10] = onSubmit;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const t7 = placeholder ?? strings.chat?.placeholder ?? "Ihre Frage...";
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      outline: "none"
    };
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== handleKeyDown || $[14] !== isLoading || $[15] !== onChange || $[16] !== t7 || $[17] !== value) {
    t9 = <textarea ref={textareaRef} value={value} onChange={onChange} onKeyDown={handleKeyDown} placeholder={t7} disabled={isLoading} rows={1} style={t8} className="flex-1 resize-none overflow-hidden border-0 border-b border-outline-variant bg-transparent px-0 py-3 text-base sm:text-sm min-h-[44px] placeholder:text-on-surface-variant/40 focus:border-secondary disabled:opacity-50 transition-colors" />;
    $[13] = handleKeyDown;
    $[14] = isLoading;
    $[15] = onChange;
    $[16] = t7;
    $[17] = value;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== isLoading || $[20] !== value) {
    t10 = isLoading || !value.trim();
    $[19] = isLoading;
    $[20] = value;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Send className="h-4 w-4" />;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t10) {
    t12 = <button type="submit" disabled={t10} className="shrink-0 rounded-full bg-secondary-fixed p-2.5 text-on-secondary-fixed disabled:opacity-40 disabled:cursor-not-allowed transition-colors" aria-label={strings.chat?.send ?? "Senden"}>{t11}</button>;
    $[23] = t10;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t12 || $[26] !== t9) {
    t13 = <div className="flex items-end gap-2">{t9}{t12}</div>;
    $[25] = t12;
    $[26] = t9;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t13 || $[29] !== t6) {
    t14 = <form onSubmit={t6} className="shrink-0 bg-surface-container-low px-4 py-3">{t13}</form>;
    $[28] = t13;
    $[29] = t6;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  return t14;
}